import React, { useState } from "react";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import { GlobalWorkerOptions } from "pdfjs-dist";
import "react-quill/dist/quill.snow.css";
import { Stack, Select, Input, Text } from "@chakra-ui/react";
import ReusableDrawer from "components/Drawers/ReusableDrawer";
import headerImage from "../../../../../assets/img/longarrow.jpg";
import GradientButton from "components/Buttons/GradientButton.js";
import ReusableInput from "components/Inputs/ReusableInput.js";
import { useCustomNotification } from "components/Toasts/OpenNotification";

const AddPolicyDrawer = ({ isOpen, onClose, onAddPolicy }) => {
  const notify = useCustomNotification();
  const [formData, setFormData] = useState({
    title: "",
    customTitle: "",
    content: "",
    dateAdded: new Date().toLocaleDateString(),
  });
  const [loadingPdf, setLoadingPdf] = useState(false);

  // Set PDF.js worker src
  GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js`;




  const handleSelectChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      title: value,
    }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      notify({
        type: "error",
        message: "Invalid File",
        description: "Please upload a valid PDF file.",
      });
      e.target.value = null;
      return;
    }

    setLoadingPdf(true);
    notify({
      type: "loading",
      description: "Extracting PDF content...",
      duration: 10,
    });

    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

      let textContent = "";

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const content = await page.getTextContent();

        const lines = {};
        for (const item of content.items) {
          const y = Math.round(item.transform[5]);
          const x = item.transform[4];
          if (!lines[y]) lines[y] = [];
          lines[y].push({ str: item.str, x });
        }

        const sortedY = Object.keys(lines)
          .map(Number)
          .sort((a, b) => b - a);

        const pageText = sortedY
          .map((y) =>
            lines[y]
              .sort((a, b) => a.x - b.x)
              .map((item) => item.str)
              .join(" ")
          )
          .join("\n");

        textContent += pageText + "\n\n";
      }

      const htmlFormatted = textContent
        .split("\n")
        .map((line) => `<p>${line.trim()}</p>`)
        .join("");

      setFormData((prev) => ({
        ...prev,
        content: htmlFormatted,
      }));

      notify({
        type: "success",
        message: "PDF Uploaded",
        description: "PDF content extracted successfully.",
      });
    } catch (err) {
      console.error("PDF extract error:", err);
      notify({
        type: "error",
        message: "Upload Failed",
        description: "Failed to extract PDF content.",
      });
    } finally {
      setLoadingPdf(false);
      e.target.value = null;
    }
  };


  const handleSubmit = () => {
    const finalTitle =
      formData.title === "Other"
        ? (formData.customTitle || "").trim()
        : formData.title;

    if (!finalTitle) {
      alert("Please select a policy type or enter a custom title.");
      return;
    }
    if (!formData.content) {
      alert("Please provide policy content.");
      return;
    }

    onAddPolicy({ ...formData, title: finalTitle });
    onClose();
  };


  return (
    <ReusableDrawer
      isOpen={isOpen}
      onClose={onClose}
      headerImage={headerImage}
      headerContent={<h2>Add New Policy</h2>}
      bodyContent={
        <Stack spacing={4}>
          <Select
            name="title"
            value={formData.title}
            onChange={(e) => {
              const value = e.target.value;
              setFormData((prev) => ({
                ...prev,
                title: value,
                customTitle: value === "Other" ? prev.customTitle || "" : "",
              }));
            }}
            placeholder="Select Policy Type"
          >
            <option value="Privacy Policy">Privacy Policy</option>
            <option value="Terms and Conditions">Terms and Conditions</option>
            <option value="Rules for Senders">Rules for Senders</option>
            <option value="Parcel Verification by the Courier">Parcel Verification by the Courier</option>
            <option value="Couriers Do Not Accept">Couriers Do Not Accept</option>
            <option value="Freight">Freight</option>
            <option value="Blocking an Account">Blocking an Account</option>
            <option value="Other">Other</option>
          </Select>

          {formData.title === "Other" && (
            <Input
              placeholder="Enter custom policy title"
              value={formData.customTitle || ""}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, customTitle: e.target.value }))
              }
            />
          )}


          <ReusableInput
            label="Policy Content"
            name="content"
            value={formData.content}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, content: e.target.value }))
            }
            placeholder="Enter the policy content"
            type="richtext"
          />

          {/* PDF Upload Input */}
          <Stack>
            <Text fontWeight="semibold" fontSize="sm" color="#8D93A1">Upload PDF (extracts content)</Text>
            <Input
              type="file"
              accept="application/pdf"
              onChange={handleFileChange}
              disabled={loadingPdf}
            />
            {loadingPdf && (
              <Text fontSize="sm" color="blue.500">
                Extracting content from PDF...
              </Text>
            )}
          </Stack>
        </Stack>
      }
      footerContent={
        <Stack direction="row" spacing={4} justifyContent="flex-end">
          <GradientButton
            onClick={onClose}
            fontSize={"15px"}
            width={"100px"}
            height={"40px"}
            isDisabled={loadingPdf}
          >
            Cancel
          </GradientButton>
          <GradientButton
            onClick={handleSubmit}
            fontSize={"15px"}
            width={"100px"}
            height={"40px"}
            isDisabled={loadingPdf}
          >
            Save
          </GradientButton>
        </Stack>
      }
    />
  );
};

export default AddPolicyDrawer;
